// The trade ticket's questions, and the checks the app makes before it sends a
// ticket. Salesforce makes every one of these checks again when it saves (the
// wizard's own logic in TradeRequestExt), so these are here to tell the
// advisor sooner, never to decide on their own. Where the app and Salesforce
// disagree, Salesforce's answer is the one shown.

// Field API name -> how to ask for it. `picklist` names the picklist the
// endpoint returns, so the app never offers a value Salesforce would refuse.
// `retired` questions are no longer asked; they stay named so older requests
// still read correctly.
export const FIELDS = {
  Description__c: { label: 'Additional instructions for Investment Management', kind: 'textarea', rows: 4,
    help: 'Only what the questions above did not cover. Investment Management reads the answers first, so there is no need to repeat them.' },
  Special_Instructions__c: { label: 'Special instructions', kind: 'textarea', rows: 3, retired: true },
  Create_Operations_Case__c: { label: 'Also open an Operations case', kind: 'checkbox', retired: true },

  // Raising cash goes through the Move Money wizard in Salesforce now; these stay for requests made before.
  Trade_Amount__c: { label: 'Amount to raise', kind: 'currency', retired: true },
  Cash_Needed_By__c: { label: 'Cash needed by', kind: 'date', retired: true },
  Why_we_are_freeing_it_up__c: { label: 'Why we are freeing it up', kind: 'text', retired: true },
  Where_is_the_money_going__c: { label: 'Where the money is going', kind: 'text', retired: true },
  Is_Money_Leaving_the_Account__c: { label: 'Is money leaving the account?', kind: 'picklist', picklist: 'Is_Money_Leaving_the_Account__c', retired: true },
  Who_is_moving_the_money__c: { label: 'Who is moving the money?', kind: 'picklist', picklist: 'Who_is_moving_the_money__c', retired: true },
  Source_of_Cash__c: { label: 'Where Investment Management can raise it from', kind: 'picklist', picklist: 'Source_of_Cash__c', retired: true },
  Federal_Tax_Withholding__c: { label: 'Federal tax withholding', kind: 'currency', retired: true },
  State_Tax_Withholding__c: { label: 'State tax withholding', kind: 'currency', retired: true },
  Capital_Gain_Concerns__c: { label: 'Capital gain concerns', kind: 'text', retired: true },

  Dollar_Amount_Of_Investment__c: { label: 'Amount to invest', kind: 'currency' },
  Fully_Invest_Cash__c: { label: 'Fully invest the cash?', kind: 'picklist', picklist: 'Fully_Invest_Cash__c' },
  Cash_to_Set_Aside__c: { label: 'Cash to set aside', kind: 'currency' },
  Cash_to_Set_Aside_Reason__c: { label: 'Why that cash is set aside', kind: 'text' },
  Cash_to_Set_Aside_Additional_Notes__c: { label: 'Notes on the cash set aside', kind: 'textarea', rows: 2 },

  Has_all_the_money_come_in__c: { label: 'Has all the money come in?', kind: 'picklist', picklist: 'Has_all_the_money_come_in__c', retired: true },
  Legacy_positions_to_be_kept_unmanaged__c: { label: 'Legacy positions to keep unmanaged?', kind: 'picklist', picklist: 'Legacy_positions_to_be_kept_unmanaged__c', retired: true },
  Partial_Transfer__c: { label: 'Partial transfer', kind: 'checkbox', retired: true },
  Model_Modifications__c: { label: 'Sleeves and modifications', kind: 'multipicklist', picklist: 'Model_Modifications__c',
    help: 'The Plus and liquid alternatives sleeves, sustainability, X-China, and national or CA munis.' },
  Advisor_Confirmed_Systematics_and_RMDs__c: { label: 'Systematics and RMDs on file are correct?', kind: 'picklist', picklist: 'Advisor_Confirmed_Systematics_and_RMDs__c',
    help: 'The cases and cash reserves on file are shown with each account above.' },
  Updates_to_Systematics_and_RMDs__c: { label: 'Updates to systematics and RMDs', kind: 'textarea', rows: 2 },

  Annual_Capital_Gains_Budget__c: { label: 'Long-term capital gains budget for this year', kind: 'currency',
    help: 'The long-term gains Investment Management may realize this year. Short-term gains are not budgeted; if there is a reason to realize any, say so in the additional instructions.' },
  Requested_Model__c: { label: 'Model to move to', kind: 'model',
    help: 'If the household has no open model change case, sending this opens one.' },
  Allowable_gains_for_this_request_beyond__c: { label: 'Gains allowed beyond the budget for this request', kind: 'currency' },
  Carry_Forward_Loss__c: { label: 'Carry-forward loss', kind: 'currency', retired: true },
  Long_Term_Capital_Gains_Rate__c: { label: 'Long-term capital gains rate (%)', kind: 'percent', retired: true },
  Shot_Term_Capital_Gains_Rate__c: { label: 'Short-term capital gains rate (%)', kind: 'percent', retired: true },
  State_Tax_Rate__c: { label: 'State tax rate (%)', kind: 'percent', retired: true },
  Tax_Loss_Harvest__c: { label: 'Tax-loss harvest', kind: 'checkbox', retired: true },

  // An initial allocation's questions for one account, saved on that account's row of the request.
  Managed_By__c: { label: 'Managed by', kind: 'picklist', picklist: 'Managed_By__c' },
  Ready_To_Invest__c: { label: 'Good to invest as soon as it is funded', kind: 'checkbox' },
  Wait_For_All_Transfers__c: { label: 'Wait for every transfer before allocating?', kind: 'picklist', picklist: 'Wait_For_All_Transfers__c' },
  Cash_To_Keep__c: { label: 'More cash to keep in this account', kind: 'currency',
    help: 'Beyond the cash reserves and systematics already on file, shown above. For example money set aside for a conversion or a purchase.' },
  Cash_To_Keep_Reason__c: { label: 'Why that cash is kept', kind: 'text' },
  Cash_Flows__c: { label: 'Regular deposits or large withdrawals?', kind: 'picklist', picklist: 'Cash_Flows__c',
    help: 'Investment Management groups and trades an account differently when money moves in or out of it often.' },
  OK_To_Sell_Everything__c: { label: 'Investment Management may sell everything in this account', kind: 'checkbox' },
  Positions_Not_To_Sell__c: { label: 'Positions not to sell', kind: 'textarea', rows: 2,
    help: 'If anything must stay, list it here instead of ticking the box above. Positions held back for gains are covered by the gains budget.' },
  Gains_Budget_This_Year__c: { label: 'Long-term capital gains budget this year', kind: 'currency',
    help: 'Defaults to 2% of the account value. Raise it to allow more gains; there is no separate carry-forward loss to enter.' },
  Pureflow__c: { label: 'Keep this account enrolled in PureFlow', kind: 'optout', picklist: 'Pureflow__c',
    help: 'Every account is enrolled; untick to opt it out.' },
  Account_Notes__c: { label: 'Anything else about this account', kind: 'textarea', rows: 2 },
};

// ---------------------------------------------------------------- who manages an account

/** The managers an advisor can choose for an account on an initial allocation, each with its own questions. */
export const MANAGERS = ['Pure', 'Vise', 'PIMCO', 'All Cash', 'Pontera'];
/** Managers an Operations case can set an account up with, when Salesforce does not show it there yet. */
export const SET_UP_MANAGERS = ['Vise', 'PIMCO'];
/** Accounts these managers run are not sold down into a Pure model, so they need no sell-everything answer. */
const NOT_SOLD_DOWN = new Set(['All Cash', 'Pontera']);

const EQUITY_OPTIONS = ['10%', '20%', '30%', '40%', '50%', '60%', '70%', '80%', '90%', '100%'];
const GAINS_CHOICES = term => [`Do not realize additional net ${term} gains`, 'Set a maximum amount', `Do not limit ${term} gains`];
const GAINS_QUESTIONS = [
  { key: 'shortTermBudget', label: 'Short-term gains', kind: 'choice', options: GAINS_CHOICES('short-term'), default: GAINS_CHOICES('short-term')[0] },
  { key: 'shortTermMax', label: 'Short-term maximum this year', kind: 'currency', showWhen: { shortTermBudget: 'Set a maximum amount' }, required: true },
  { key: 'longTermBudget', label: 'Long-term gains', kind: 'choice', options: GAINS_CHOICES('long-term'), default: 'Set a maximum amount' },
  { key: 'longTermMax', label: 'Long-term maximum this year', kind: 'currency', showWhen: { longTermBudget: 'Set a maximum amount' }, required: true },
];

/**
 * The questions that belong to each manager, as Investment Management laid them out. Their answers travel as
 * labelled pairs, so the questions can change without a Salesforce field for each.
 */
export const MANAGER_QUESTIONS = {
  Pure: [],
  Vise: [
    { key: 'strategy', label: 'Vise strategy', kind: 'text', required: true, help: 'As on the Vise proposal, for example Direct Index Core Equity.' },
    { key: 'restrictTickers', label: 'Buying restrictions: companies or tickers', kind: 'text', help: 'Restricted positions are not bought, and are sold if currently held.' },
    { key: 'restrictSectors', label: 'Buying restrictions: sectors', kind: 'text' },
    { key: 'restrictCountries', label: 'Buying restrictions: countries', kind: 'text' },
    { key: 'restrictValues', label: 'Buying restrictions: values (ESG)', kind: 'text' },
    { key: 'targetCash', label: 'Target cash allocation (%)', kind: 'percent', default: '1', required: true,
      help: 'Vise recommends 1%; the minimum is 0.5%. Vise holds no reserve for distributions and auto-invests inflows.' },
    ...GAINS_QUESTIONS,
  ],
  PIMCO: [
    { key: 'strategy', label: 'PIMCO strategy', kind: 'text', required: true, help: 'For example a corporate bond ladder, a CA muni ladder, or Core.' },
    { key: 'targetCash', label: 'Target cash allocation (%)', kind: 'percent', default: '1', required: true },
    ...GAINS_QUESTIONS,
  ],
  'All Cash': [
    { key: 'cashHeldAs', label: 'How should the cash be held?', kind: 'choice', required: true,
      options: ['Sweep cash (the client can use it like checking)', 'Transactional money market (T+1 to access)', 'T-bill ladder'] },
    { key: 'muniMoneyMarket', label: 'Hold it in a municipal money market fund', kind: 'check' },
    { key: 'autoInvestInflows', label: 'Automatically invest future cash inflows the same way', kind: 'check' },
  ],
  Pontera: [
    { key: 'equityAllocation', label: 'Equity allocation', kind: 'choice', options: EQUITY_OPTIONS, required: true,
      help: 'Pontera trades the account directly, so the model and one holding decision are all it needs.' },
    { key: 'companyStock', label: 'Does this account hold company stock the client wants to keep?', kind: 'choice', options: ['No', 'Yes'], required: true },
    { key: 'companyStockKeep', label: 'How much should continue to be held?', kind: 'text', showWhen: { companyStock: 'Yes' }, required: true,
      help: 'For example 320 shares, or $18,400.' },
  ],
};

/** The manager Salesforce shows for an account, or Pure. 'Other SMA' and 'Non-discretionary' come only from Salesforce. */
export function managerOf(account) {
  return (account && account.managedBy) || 'Pure';
}

/** The manager the advisor chose for an account on this ticket, or the one Salesforce shows. */
export function chosenManager(account, given) {
  return (given && given.Managed_By__c) || managerOf(account);
}

/** Whether the advisor's choice of manager needs Operations to set the account up first. */
export function needsSetUp(account, given) {
  const chosen = chosenManager(account, given);
  return SET_UP_MANAGERS.includes(chosen) && managerOf(account) !== chosen;
}

/** A manager question is shown when the answer it depends on has been given. */
export function managerQuestionShown(q, answers) {
  if (!q.showWhen) return true;
  return Object.entries(q.showWhen).every(([k, v]) => (answers || {})[k] === v);
}

/** The manager-specific answers as labelled pairs, the way they travel to Salesforce. */
export function managerAnswerPairs(manager, answers) {
  const out = [];
  for (const q of MANAGER_QUESTIONS[manager] || []) {
    if (!managerQuestionShown(q, answers)) continue;
    const v = (answers || {})[q.key] ?? q.default;
    if (q.kind === 'check') { if (v) out.push({ label: q.label, value: 'Yes' }); continue; }
    if (blank(v)) continue;
    const shown = q.kind === 'currency' ? money(v) : q.kind === 'percent' ? `${v}%` : String(v);
    out.push({ label: q.label, value: shown });
  }
  return out;
}

// ---------------------------------------------------------------- an initial allocation's questions

/**
 * An initial allocation's questions for one account, by who manages it. A Pure-managed taxable account is "a
 * much different beast": cash needs, gains, consolidations and money passing through, so it gets the full set
 * up front. A Pure-managed retirement account is invested on its first deposit and grouped by Investment
 * Management, so it needs the go-ahead, any cash to hold back, and the rest only if there is something to say.
 * Every account Investment Management sells down answers whether it may sell everything. Vise and PIMCO ask
 * their own questions (MANAGER_QUESTIONS), and All Cash and Pontera accounts are not sold down at all.
 */
export function rowQuestionGroups(account, manager = null) {
  const who = manager || managerOf(account);
  if (who === 'Pure') {
    return isTaxable(account)
      ? { main: ['Wait_For_All_Transfers__c', 'Cash_To_Keep__c', 'Cash_To_Keep_Reason__c', 'Cash_Flows__c', 'OK_To_Sell_Everything__c',
        'Positions_Not_To_Sell__c', 'Gains_Budget_This_Year__c', 'Pureflow__c', 'Account_Notes__c'], more: [] }
      : { main: ['Ready_To_Invest__c', 'Cash_To_Keep__c', 'Cash_To_Keep_Reason__c', 'OK_To_Sell_Everything__c', 'Positions_Not_To_Sell__c', 'Pureflow__c'],
        more: ['Cash_Flows__c', 'Account_Notes__c'] };
  }
  if (NOT_SOLD_DOWN.has(who)) return { main: ['Account_Notes__c'], more: [] };
  return { main: ['OK_To_Sell_Everything__c', 'Positions_Not_To_Sell__c', 'Account_Notes__c'], more: [] };
}

/** Every Salesforce question an initial allocation can ask about this account, in order. */
export function rowQuestionsFor(account, manager = null) {
  const g = rowQuestionGroups(account, manager);
  return [...g.main, ...g.more];
}

/** The gains budget Investment Management assumes for a taxable account: 2% of its value, once it is funded. */
export function defaultGainsBudget(account) {
  const v = Number(account && account.value);
  return Number.isFinite(v) && v > 0 ? Math.round(v * 0.02) : null;
}

/** "Taxable" or "Retirement", the two kinds of account the ticket treats differently. */
export function taxLabel(account) {
  if (!account || !account.taxType) return '';
  return isTaxable(account) ? 'Taxable' : account.taxType === 'Qualified' ? 'Retirement' : account.taxType;
}

// ---------------------------------------------------------------- what the household's situation allows

export const INITIAL = 'Initial Allocation';

/**
 * Where one account stands with its first allocation: it still needs one, one is in progress, or it is
 * allocated. The endpoint says which; older answers carried only the two flags.
 */
export function allocationOf(account) {
  if (!account) return 'allocated';
  if (account.allocation) return account.allocation;
  if (account.initialAllocationOpen) return 'inProgress';
  if (account.needsInitialAllocation) return 'needs';
  return 'allocated';
}

/**
 * The household's situation, which decides what the ticket assumes:
 * - 'new': nothing here is allocated yet, so the only request that makes sense is the initial allocation;
 * - 'mixed': some accounts are allocated and others still wait for their first allocation (new accounts for
 *   an existing client), so the initial allocation is assumed but the other requests are a step away;
 * - 'allocated': every account has been allocated, so an initial allocation is not offered.
 */
export function householdSituation(accounts) {
  const states = (accounts || []).map(allocationOf);
  const needs = states.includes('needs');
  const allocated = states.includes('allocated');
  if (!allocated && (needs || states.includes('inProgress'))) return 'new';
  if (needs && allocated) return 'mixed';
  return 'allocated';
}

/** The request types to offer for a situation. `override` is the advisor asking for every type. */
export function typesFor(situation, override = false) {
  if (override) return REQUEST_TYPES;
  if (situation === 'new') return REQUEST_TYPES.filter(t => t.value === INITIAL);
  if (situation === 'allocated') return REQUEST_TYPES.filter(t => t.value !== INITIAL);
  return REQUEST_TYPES;
}

/** The accounts an initial allocation starts with: every account that still needs one and can be traded. */
export function accountsNeedingAllocation(accounts) {
  return (accounts || []).filter(a => allocationOf(a) === 'needs' && !accountBlocker(a, INITIAL)).map(a => a.id);
}

const TRADES_A_MODEL = new Set(['Model Change', 'Allocation New Cash', 'Free Up Cash']);

/**
 * Each chosen account's answers, only its own questions, without blanks, plus who manages it and that
 * manager's answers. A Pure account left on the default is enrolled in PureFlow. Accounts held out for
 * set-up are not here: they go as setupCases instead.
 */
export function rowAnswersToSend(accountAnswers, chosen) {
  const out = {};
  for (const a of chosen) {
    const given = (accountAnswers || {})[a.id] || {};
    if (needsSetUp(a, given)) continue;
    const manager = chosenManager(a, given);
    const row = { Managed_By__c: manager };
    for (const f of rowQuestionsFor(a, manager)) {
      const v = given[f];
      if (f === 'Pureflow__c') { row[f] = v === 'Opted out' ? 'Opted out' : 'Enrolled'; continue; }
      if (f === 'Gains_Budget_This_Year__c' && blank(v)) { const d = defaultGainsBudget(a); if (d) row[f] = String(d); continue; }
      if (FIELDS[f].kind === 'checkbox') { if (v) row[f] = true; continue; }
      if (!blank(v)) row[f] = v;
    }
    const pairs = managerAnswerPairs(manager, given.manager);
    if (pairs.length) row.kindAnswers = pairs;
    out[a.id] = row;
  }
  return out;
}

/** The accounts the advisor held out so Operations can set them up first, as the ticket sends them. */
export function setUpsToSend(accountAnswers, chosen) {
  const out = [];
  for (const a of chosen) {
    const given = (accountAnswers || {})[a.id] || {};
    if (!needsSetUp(a, given)) continue;
    out.push({ accountId: a.id, managedBy: chosenManager(a, given), strategy: given.setupStrategy || '', note: given.setupNote || '' });
  }
  return out;
}

// The request types Investment Management works from, in their order and under their names, each mapped to the
// value Salesforce stores. Raising cash is not here: the client's cash needs go through the Move Money wizard
// in Salesforce, which opens the Operations case and the trade request together. Salesforce's other values
// (Rebalance, Add Accounts to Group) are not offered either; a request they would have covered goes in as
// Special Instructions.
export const REQUEST_TYPES = [
  { value: 'Initial Allocation', label: 'Initial Allocation', hint: 'The first investment of accounts that have never been allocated' },
  { value: 'Model Change', label: 'Model Change', hint: 'Move allocated accounts to a different model' },
  { value: 'See Special Instructions', label: 'Special Instructions', hint: 'Anything the other requests do not cover. Cash the client needs goes through the Move Money wizard in Salesforce, not here.' },
  { value: 'Allocation New Cash', label: 'Invest Cash', hint: 'Put new cash to work in an allocated account' },
];

// Investment Management's names for values the ticket no longer offers but older requests carry.
const OLDER_TYPE_LABELS = { 'Free Up Cash': 'Raise Cash' };

/** A request's types under Investment Management's names, for anything stored before or outside the four. */
export function typeLabels(types) {
  return (types || []).map(t => (REQUEST_TYPES.find(r => r.value === t) || { label: OLDER_TYPE_LABELS[t] || t }).label).join(', ');
}

export const QUESTIONS_BY_TYPE = {
  'Free Up Cash': ['Trade_Amount__c', 'Cash_Needed_By__c', 'Why_we_are_freeing_it_up__c', 'Where_is_the_money_going__c',
    'Is_Money_Leaving_the_Account__c', 'Who_is_moving_the_money__c', 'Source_of_Cash__c',
    'Federal_Tax_Withholding__c', 'State_Tax_Withholding__c', 'Capital_Gain_Concerns__c'],
  'Allocation New Cash': ['Dollar_Amount_Of_Investment__c', 'Fully_Invest_Cash__c', 'Cash_to_Set_Aside__c',
    'Cash_to_Set_Aside_Reason__c', 'Cash_to_Set_Aside_Additional_Notes__c'],
  // Cash, transfers, positions and gains are asked account by account on an initial allocation
  // (rowQuestionsFor); Salesforce derives the request's own "has all the money come in" from those answers.
  'Initial Allocation': ['Model_Modifications__c', 'Advisor_Confirmed_Systematics_and_RMDs__c', 'Updates_to_Systematics_and_RMDs__c'],
  'Model Change': ['Requested_Model__c', 'Model_Modifications__c', 'Allowable_gains_for_this_request_beyond__c'],
};

// The tax question: asked whenever a taxable account is in a request other than an initial allocation, which
// asks it for each taxable account instead. The tax rates are not asked: Investment Management knows them.
export const TAX_QUESTIONS = ['Annual_Capital_Gains_Budget__c'];

const IRA_TYPES = ['IRA Traditional', 'IRA Rollover', 'IRA SEP', 'IRA Simple', 'IRA Educational', 'IRA Inherited',
  '401k', 'Individual 401k', '403b', 'Qualified Plan'];
const NOT_A_MODEL = ['Individual Bonds', 'Invested Outside Pure Model', 'Non-Discretionary Account'];

export function isTaxable(account) {
  return account && account.taxType === 'Non-Qualified';
}

/** The questions to ask for these request types and accounts, each once, in a stable order. */
export function questionsFor(types, accounts) {
  const out = [];
  const add = f => { if (!out.includes(f) && !(FIELDS[f] && FIELDS[f].retired)) out.push(f); };
  for (const t of types) for (const f of QUESTIONS_BY_TYPE[t] || []) add(f);
  if ((accounts || []).some(isTaxable) && !types.includes(INITIAL)) {
    for (const f of TAX_QUESTIONS) add(f);
  }
  return out;
}

// ---------------------------------------------------------------- the model a model change moves to

/** The parts of a Pure model code: '409 Plus' is the 400 series at 90% equity with the Plus sleeve. */
export function modelParts(code) {
  const m = String(code || '').trim().match(/^([1-5])(\d\d)(\s+Plus)?$/i);
  if (!m) return null;
  return { series: `${m[1]}00`, equity: Number(m[2]) * 10, plus: Boolean(m[3]) };
}

/** The model code for a series, an equity percentage and whether the Plus sleeve is on. */
export function composeModel(series, equity, plus = false) {
  const s = String(series || '').charAt(0);
  const e = Number(equity);
  if (!/[1-5]/.test(s) || !Number.isFinite(e) || e < 10 || e > 100 || e % 10) return '';
  return `${s}${String(e / 10).padStart(2, '0')}${plus ? ' Plus' : ''}`;
}

export const MODEL_SERIES = ['100', '200', '300', '400', '500'];
export const MODEL_EQUITY = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

/** How a model reads to an advisor: '409 Plus' is 'the 400 series at 90% equity, with the Plus sleeve'. */
export function describeModel(code) {
  const p = modelParts(code);
  if (!p) return code ? String(code) : '';
  return `${p.series} series at ${p.equity}% equity${p.plus ? ', with the Plus sleeve' : ''}`;
}

/**
 * Selecting one account of a Tamarac group selects the whole group, as the wizard requires. Outside an
 * initial allocation a request covers one group, or one account outside a group, so turning an account on
 * replaces the selection. Returns the new selection.
 */
export function toggleAccount(selected, account, allAccounts, wholeGroup = true, oneGroup = false) {
  const next = new Set(selected);
  const members = wholeGroup && account.group
    ? allAccounts.filter(a => a.group === account.group).map(a => a.id)
    : [account.id];
  const on = !selected.has(account.id);
  if (on && oneGroup) next.clear();
  for (const id of members) { if (on) next.add(id); else next.delete(id); }
  return next;
}

/** The Tamarac groups, or single accounts, a selection covers. */
export function groupsCovered(chosen) {
  return [...new Set((chosen || []).map(a => a.group || `account:${a.id}`))];
}

/**
 * Why an account cannot be in this request, or null. Without a type only Salesforce's own rule applies; with
 * one, the account has to fit it: an initial allocation is for accounts that have never been allocated, and a
 * model change or investing cash needs an account that already follows a model.
 */
export function accountBlocker(account, type = null) {
  if (!account.ipsModel || !account.riskProfile) {
    return 'No Guideline IPS or risk profile in Salesforce, so it cannot be traded through a request yet.';
  }
  const state = allocationOf(account);
  if (type === INITIAL) {
    if (account.managedBy === 'Non-discretionary') return 'Non-discretionary, so its allocation is arranged with Investment Management directly.';
    if (state === 'inProgress') return 'Its initial allocation is already with Investment Management.';
    if (state === 'allocated') return 'Already allocated, so it is not part of an initial allocation.';
  } else if (type && TRADES_A_MODEL.has(type)) {
    if (state === 'needs') return 'Not allocated yet: it needs its initial allocation first.';
    if (state === 'inProgress') return 'Its initial allocation is still with Investment Management.';
  }
  return null;
}

const blank = v => v === undefined || v === null || String(v).trim() === '' || (Array.isArray(v) && v.length === 0);

/**
 * Problems the advisor can fix before sending, as { field, message }. These
 * mirror the wizard's account rules and save checks; Salesforce runs them all
 * again on submit. A `soft` problem is a warning the advisor can send past.
 */
export function ticketProblems({ household, accounts, selected, types, answers, casesReviewed, consideredTax, accountAnswers = {} }) {
  const problems = [];
  const chosen = (accounts || []).filter(a => selected.has(a.id));

  if (household && !household.planOnFile) {
    problems.push({ field: 'household', message: 'This household has no IPS, RTQ or suitability on file, and Salesforce refuses a trade request without one.' });
  }
  if (chosen.length === 0) problems.push({ field: 'accounts', message: 'Choose at least one account.' });
  const initial = types.includes(INITIAL);
  for (const a of chosen) {
    const why = accountBlocker(a, types[0] || null);
    if (why) problems.push({ field: 'accounts', message: `${a.name}: ${why}` });
  }
  // An initial allocation covers every new account at once and Investment Management sets the groups and
  // models, so the one-group, one-model and RTQ rules apply to the other request types only.
  if (!initial && groupsCovered(chosen).length > 1) {
    problems.push({ field: 'accounts', message: 'A request covers one Tamarac group, or one account outside a group. Send one request per group.' });
  }
  const models = initial ? [] : [...new Set(chosen.map(a => a.ipsModel).filter(m => m && m !== 'RTQ'))];
  if (models.length > 1) {
    problems.push({ field: 'accounts', message: `The accounts follow different Guideline IPS models (${models.join(', ')}). A request covers one model; send one request per model.` });
  }
  const rtq = initial ? [] : chosen.filter(a => a.ipsModel === 'RTQ');
  if (rtq.length) {
    if (chosen.some(a => NOT_A_MODEL.includes(a.ipsModel))) {
      problems.push({ field: 'accounts', message: 'Accounts on Individual Bonds, Invested Outside Pure Model or Non-Discretionary cannot be in the same request as RTQ accounts.' });
    }
    if (rtq.length === chosen.length) {
      if (new Set(rtq.map(a => a.riskProfile)).size > 1) {
        problems.push({ field: 'accounts', message: 'In an all-RTQ request, every account needs the same risk profile.' });
      }
    } else {
      const risks = new Set(chosen.filter(a => a.ipsModel !== 'RTQ').map(a => a.riskProfile));
      if (rtq.some(a => !risks.has(a.riskProfile))) {
        problems.push({ field: 'accounts', message: 'An RTQ account needs the same risk profile as another account in the request.' });
      }
    }
  }

  if (types.length === 0) problems.push({ field: 'types', message: 'Choose what this request is for.' });
  // On an initial allocation the answers for each account are the instruction.
  if (!initial && blank(answers.Description__c)) problems.push({ field: 'Description__c', message: 'Write the instructions for Investment Management.' });

  if (types.includes('Free Up Cash')) {
    if (blank(answers.Why_we_are_freeing_it_up__c)) problems.push({ field: 'Why_we_are_freeing_it_up__c', message: 'Say why we are freeing up cash.' });
    if (blank(answers.Where_is_the_money_going__c)) problems.push({ field: 'Where_is_the_money_going__c', message: 'Say where the money is going.' });
    if (chosen.some(a => IRA_TYPES.includes(a.type))) {
      if (blank(answers.Federal_Tax_Withholding__c)) problems.push({ field: 'Federal_Tax_Withholding__c', message: 'Raising cash from a retirement account needs federal withholding (0 if none).' });
      if (blank(answers.State_Tax_Withholding__c)) problems.push({ field: 'State_Tax_Withholding__c', message: 'Raising cash from a retirement account needs state withholding (0 if none).' });
    }
  }
  if (types.includes('Model Change') && !modelParts(answers.Requested_Model__c)) {
    problems.push({ field: 'Requested_Model__c', message: 'Choose the model to move to: its series and equity allocation.' });
  }
  if (initial) {
    for (const a of chosen) {
      const given = accountAnswers[a.id] || {};
      const manager = chosenManager(a, given);
      const row = `row:${a.id}`;
      if (needsSetUp(a, given)) {
        if (blank(given.setupStrategy)) problems.push({ field: row, message: `${a.name}: say which ${manager} strategy it should follow, for the Operations case.` });
        continue;
      }
      if (manager === 'Pure') {
        if (isTaxable(a)) {
          if (blank(given.Wait_For_All_Transfers__c)) problems.push({ field: row, message: `${a.name}: say whether to wait for every transfer before allocating.` });
          if (blank(given.Cash_Flows__c)) problems.push({ field: row, message: `${a.name}: say whether there are regular deposits or large withdrawals.` });
        } else if (!given.Ready_To_Invest__c) {
          problems.push({ field: row, message: `${a.name}: confirm it is good to invest as soon as it is funded.` });
        }
        if (!blank(given.Cash_To_Keep__c) && Number(String(given.Cash_To_Keep__c).replace(/[^0-9.]/g, '')) > 0 && blank(given.Cash_To_Keep_Reason__c)) {
          problems.push({ field: row, message: `${a.name}: say why that cash is kept.` });
        }
      }
      if (!NOT_SOLD_DOWN.has(manager) && !given.OK_To_Sell_Everything__c && blank(given.Positions_Not_To_Sell__c)) {
        problems.push({ field: row, message: `${a.name}: confirm Investment Management may sell everything in it, or list the positions to keep.` });
      }
      for (const q of MANAGER_QUESTIONS[manager] || []) {
        if (!q.required || !managerQuestionShown(q, given.manager)) continue;
        const v = (given.manager || {})[q.key] ?? q.default;
        if (blank(v)) problems.push({ field: row, message: `${a.name}: ${q.label.replace(/\?$/, '').toLowerCase()} is needed.` });
      }
    }
    if (chosen.length && chosen.every(a => needsSetUp(a, accountAnswers[a.id] || {}))) {
      problems.push({ field: 'accounts', soft: true, message: 'Every account here is waiting to be set up, so only the Operations cases are opened; Investment Management gets nothing yet.' });
    }
  }
  if (household && household.openCaseCount > 0 && !casesReviewed) {
    problems.push({ field: 'cases', message: 'Confirm you have reviewed the household\'s open cases.' });
  }
  if (chosen.some(isTaxable) && !consideredTax) {
    problems.push({ field: 'tax', message: 'A taxable account is in this request: confirm you have considered the tax consequences.' });
  }
  return problems;
}

/** Only the answers that belong to this ticket, with blanks left out. */
export function answersToSend(answers, types, accounts) {
  const keep = new Set(['Description__c', ...questionsFor(types, accounts)]);
  const out = {};
  for (const [k, v] of Object.entries(answers)) {
    if (!keep.has(k)) continue;
    if (FIELDS[k] && FIELDS[k].kind === 'checkbox') { out[k] = Boolean(v); continue; }
    if (!blank(v)) out[k] = v;
  }
  return out;
}

/** A new key for one ticket, so a retried submit finds the request the first one saved. */
export function newTicketKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'tt-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
}

export function money(value) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(String(value).replace(/[$,]/g, ''));
  return Number.isFinite(n) ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : String(value);
}

export function showAnswer(field, value) {
  const def = FIELDS[field];
  if (value === null || value === undefined || value === '') return '';
  if (!def) return String(value);
  if (def.kind === 'currency') return money(value);
  if (def.kind === 'percent') return `${value}%`;
  if (def.kind === 'checkbox') return value ? 'Yes' : 'No';
  if (def.kind === 'multipicklist') return String(value).split(';').join(', ');
  if (def.kind === 'model') return `${value} (${describeModel(value)})`;
  if (def.kind === 'date') {
    const d = new Date(String(value) + 'T00:00:00');
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  return String(value);
}

// ---------------------------------------------------------------- Investment Management's screen

/** The buckets Investment Management's queue sorts open requests into, in the order the queue shows them. */
export const IM_BUCKETS = [
  { key: 'sentBack', title: 'Waiting on the advisor', pick: r => r.status === 'CFP Communication Required' },
  { key: 'initial', title: 'Initial allocations', pick: r => (r.types || []).includes(INITIAL) },
  { key: 'model', title: 'Model changes', pick: r => (r.types || []).includes('Model Change') },
  { key: 'raise', title: 'Raise cash', pick: r => (r.types || []).includes('Free Up Cash') || Boolean(r.fromWizard) },
  { key: 'invest', title: 'Invest cash', pick: r => (r.types || []).includes('Allocation New Cash') },
  { key: 'special', title: 'Special instructions', pick: r => (r.types || []).includes('See Special Instructions') },
  { key: 'other', title: 'Rebalances and everything else', pick: () => true },
];

/** Each open request in the first bucket that takes it. */
export function bucketRequests(requests) {
  const out = IM_BUCKETS.map(b => ({ ...b, rows: [] }));
  for (const r of requests || []) {
    const b = out.find(x => x.pick(r));
    if (b) b.rows.push(r);
  }
  return out;
}

/** The follow-up cases a trader can open from a request, each the case Investment Management opens by hand today. */
export const FOLLOW_UPS = [
  { kind: 'dividendReinvestment', label: 'Turn off dividend reinvestment', help: 'An INV case due tomorrow, for all positions in the account.' },
  { kind: 'intervalFund', label: 'Interval fund tender', needsTicker: true, needsDate: true, help: 'A "Tender Date" case for the fund, due on the tender date.' },
  { kind: 'worthlessSecurities', label: 'Worthless securities', needsTicker: true, help: 'A case to remove the position.' },
  { kind: 'futureTrade', label: 'Trade later', needsDetail: true, needsDate: true, help: 'For example a sale once a lot turns long-term.' },
];
